'use client';

import { Suspense, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useAuth } from '@/lib/auth/provider';
import { setSession } from '@/lib/auth/session';
import { useFirmPublic } from '@/lib/api/firm-public';
import { safeLanding, safeReturnTo } from '@/lib/auth/safe-return-to';

// Dev quick-login — the single seeded dev/CI account (not a demo persona).
// Human QA/demo signs in as Fee Earner One via the firm's own SSO instead.
const DEV_USERS = [
  { email: 'e2e@examplefirm.test', label: 'E2E Test User', role: 'Admin' },
];

/**
 * The `?error=` vocabulary is the API's: `GET /api/auth/login` redirects
 * with `workspace_not_connected` (`routes/auth.ts:435`), `GET /callback`
 * with `invalid_credentials` (`:469`), `mfa_required` / `mfa_enrolment_required` /
 * `mfa_enrolment_unverified`
 * (`:488`) or the
 * provider's own `error` param (`:463`), and the dashboard callback page
 * with `auth_failed` / `missing_params`.
 *
 * FIXED ALLOW-LIST, and an unrecognised code gets one generic sentence
 * (review I2, Ruling O11). The raw parameter is never echoed: it is
 * unbounded attacker-chosen text, and while React escapes it, rendering
 * `?error=Your+account+is+locked,+call+0800…` inside the styled warning
 * box of the firm's real login page is a phishing primitive on its own.
 */
const ERROR_COPY: Record<string, string> = {
  workspace_not_connected:
    'This firm’s workspace is not connected yet, so sign-in is unavailable. An administrator needs to complete the workspace consent first.',
  mfa_required:
    'Multi-factor authentication is required for your account. Complete it with your provider, then try again.',
  // Google's only signal is 2-Step Verification ENROLMENT (no per-sign-in
  // claim), so its refusal asks the user to turn 2SV on (Google parity G2).
  mfa_enrolment_required:
    'Your account must have 2-Step Verification turned on with your provider before you can sign in. Turn it on, then try again.',
  // The enrolment lookup itself failed (usually a workspace configuration
  // issue on the firm's side), so the user may already be enrolled.
  mfa_enrolment_unverified:
    'We could not confirm 2-Step Verification for your account. Turn it on with your provider, or contact your administrator, then try again.',
  invalid_credentials: 'That sign-in could not be verified. Please start again.',
  token_exchange_failed: 'Your provider rejected the sign-in. Please try again.',
  auth_failed: 'Sign-in failed. Please try again.',
  missing_params: 'That sign-in link was incomplete. Please start again.',
  // The provider's own OAuth error codes, which `GET /callback` forwards
  // verbatim (`routes/auth.ts:463`).
  access_denied: 'The sign-in was cancelled or refused by your provider. Please try again.',
  consent_required: 'Your provider needs you to grant access before signing in. Please try again.',
  login_required: 'Your provider needs you to sign in again. Please try again.',
  interaction_required: 'Your provider needs another step before signing in. Please try again.',
  temporarily_unavailable: 'Your provider is temporarily unavailable. Please try again shortly.',
  server_error: 'Your provider reported an error. Please try again.',
  // First-admin bootstrap (design spec 2026-09-22, Component 5) — `GET
  // /callback` sends a non-designated address back here with this code
  // (`routes/auth.ts`) when the firm is still PENDING.
  onboarding_admin_only: 'Only this firm’s bootstrap administrator can sign in until the workspace is connected.',
  // Workspace parity audit S2 — `GET /callback` sends a guest account, an
  // account from another directory, or a first sign-in from outside the
  // firm's domains back here (`routes/auth.ts`).
  account_not_permitted: 'This account cannot sign in to this firm. Use your firm work account, or ask your administrator.',
  // `GET /callback` (`routes/auth.ts`) when the firm's token-cache lock
  // stayed busy past the login wait budget — retryable.
  signin_busy: 'Sign-in busy, please try again.',
};

const GENERIC_ERROR_COPY = 'Sign-in failed. Please try again, or contact your administrator if it keeps happening.';

/**
 * Spec §2.2 — the same explanation the API's `workspace_not_connected`
 * redirect carries, shown INSTEAD of a button that cannot work when the
 * firm has no provider or has not granted consent.
 */
const NOT_CONNECTED_COPY = ERROR_COPY.workspace_not_connected;

/**
 * First-admin bootstrap (design spec 2026-09-22, Component 5) — shown
 * instead of `NOT_CONNECTED_COPY` when the firm IS eligible for a
 * bootstrap sign-in (PENDING + a `bootstrapAdminEmail` configured): the
 * button still renders (the designated admin can use it), but everyone
 * else is told why it will refuse them.
 */
const BOOTSTRAP_ADMIN_ONLY_COPY = ERROR_COPY.onboarding_admin_only;

function MicrosoftMark() {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 21 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

function GoogleMark() {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.2 26.7 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.2 5.2C37 40.2 44 35 44 24c0-1.2-.1-2.4-.4-3.5z"
      />
    </svg>
  );
}

const SSO_BUTTON_CLASS =
  'flex w-full cursor-pointer items-center justify-center gap-3 rounded-lg bg-accent px-4 py-3 text-sm font-medium text-white transition-colors duration-200 hover:bg-accent-ink disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/13 focus-visible:ring-offset-2';


function LoginPageContent() {
  const { login } = useAuth();
  const searchParams = useSearchParams();
  const firm = useFirmPublic();
  const isMockAuth = process.env.NEXT_PUBLIC_MOCK_AUTH === 'true';

  const [showDevLogin, setShowDevLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const errorCode = searchParams.get('error');
  // Where THIS sign-in should land: the deep link the middleware (or the
  // layout's sign-out bounce) put on this page's URL. Handed to the API so
  // it is bound to this attempt's OAuth state (2026-10-08 follow-up) — the
  // button used to send none, and the callback then honoured a return cookie
  // an earlier attempt in another tab had left behind. Validated here too:
  // the URL is attacker-controllable, and a bad value means no returnTo at
  // all (the default landing), never a pass-through.
  const returnTo = safeReturnTo(searchParams.get('returnTo')) ?? undefined;
  const signIn = () => login(returnTo);
  // `ERROR_COPY` is a plain object, so a bare index (`ERROR_COPY[errorCode]`) resolves an
  // inherited key too — `?error=__proto__`/`constructor`/`toString` would then hand React a
  // non-string (an object or a function) to render, and it throws. `hasOwnProperty` restricts
  // the lookup to the fixed allow-list itself.
  const errorMessage = errorCode
    ? Object.prototype.hasOwnProperty.call(ERROR_COPY, errorCode)
      ? ERROR_COPY[errorCode]
      : GENERIC_ERROR_COPY
    : null;

  // Task 2 carry-in — no placeholder firm. This page is served on a firm's
  // OWN host, so a name we could not read is a name we do not know; naming
  // some other firm on it is worse than naming none, and the heading and
  // the footer line below both collapse rather than substitute.
  const firmName = firm.data?.name?.trim() || null;
  const provider = firm.data?.provider ?? null;
  // Spec §2.2 — a firm with no provider row, or one whose consent has not
  // been granted (or was revoked), cannot complete a sign-in: the API
  // would bounce straight back with `workspace_not_connected`. Show that
  // explanation rather than a dead button.
  const connected = provider !== null && firm.data?.consentStatus === 'GRANTED';
  // First-admin bootstrap (design spec 2026-09-22, Component 5) — a
  // PENDING firm with a bootstrap admin configured still gets the sign-in
  // button: `GET /login` can build a real authorize URL for it now
  // (`services/auth.ts#workspaceForLogin`), and the callback is what
  // actually refuses everyone except that one address. Only PENDING is
  // eligible — same rule as `workspaceForLogin`/the admission check:
  // REVOKED (or no connection at all) keeps the old dead-button-free
  // state, matching `services/auth.ts`'s own "only PENDING" gate.
  const bootstrapEligible =
    firm.data?.consentStatus === 'PENDING' && firm.data?.bootstrapAdminConfigured === true;
  // A failed lookup is NOT a "not connected" verdict. The API is the
  // authority on whether this firm can sign in, and it answers
  // `workspace_not_connected` itself when it cannot — so a public GET that
  // fails falls back to a provider-neutral button rather than locking a
  // perfectly healthy firm out of its own login page.
  const lookupFailed = firm.isError;
  const notConnectedCopy = bootstrapEligible ? BOOTSTRAP_ADMIN_ONLY_COPY : NOT_CONNECTED_COPY;
  // M3 — a firm bounced back by the API's `workspace_not_connected`
  // redirect would otherwise read the identical sentence twice: once in
  // the banner and once as the explanation shown alongside (or instead
  // of) the button.
  const notConnectedShown = !firm.isLoading && !lookupFailed && !connected;
  const showErrorBanner =
    errorMessage !== null && !(notConnectedShown && errorMessage === notConnectedCopy);

  async function handleDevLogin(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const res = await fetch(`/api/auth/dev-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error?.message || 'Login failed');
        return;
      }

      setSession({
        accessToken: data.accessToken,
        refreshToken: '',
        expiresAt: Date.now() + data.expiresIn * 1000,
        user: data.user,
      });

      // Post-login honours the deep link the middleware captured as
      // `returnTo`, falling back to Home (10a Command desk).
      //
      // `safeLanding` is the ONE open-redirect predicate on this side
      // (Ruling O11): it mirrors the server's `RETURN_TO_RE` character for
      // character, bounds the length, re-checks after one decode and then
      // resolves against our own origin. The design branch added a second,
      // looser `isSameOriginPath` here, so the dev-login path validated by a
      // different rule than the SSO path (review M1) — there is one rule.
      const params = new URLSearchParams(window.location.search);
      window.location.href = safeLanding(params.get('returnTo'), '/');
    } catch {
      setError('Could not connect to the API. Is the server running?');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="w-full max-w-md rounded-lg bg-surface p-8 shadow-md">
      <div className="mb-8 text-center">
        {firmName && (
          <h1 className="font-heading text-3xl font-semibold text-ink">{firmName}</h1>
        )}
        <p className="mt-2 text-sm text-muted">
          Practice Management Platform
        </p>
      </div>

      {showErrorBanner && (
        <p className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {errorMessage}
        </p>
      )}

      {showDevLogin ? (
        <form onSubmit={handleDevLogin} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-content">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email address"
              required
              className="mt-1 block w-full rounded-lg border border-line-firm px-3 py-2 text-sm text-ink placeholder:text-faint-ink focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {DEV_USERS.map((user) => (
              <button
                key={user.email}
                type="button"
                onClick={() => setEmail(user.email)}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                  email === user.email
                    ? 'border-accent bg-accent-wash text-accent'
                    : 'border-line text-content hover:border-line-firm hover:bg-inset'
                }`}
              >
                {user.label} ({user.role})
              </button>
            ))}
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-content">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              required
              className="mt-1 block w-full rounded-lg border border-line-firm px-3 py-2 text-sm text-ink placeholder:text-faint-ink focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>

          {error && (
            <p className="rounded-md bg-alarm-wash px-3 py-2 text-sm text-alarm">{error}</p>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="flex w-full cursor-pointer items-center justify-center rounded-lg bg-accent px-4 py-3 text-sm font-medium text-white transition-colors duration-200 hover:bg-accent-ink disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/13 focus-visible:ring-offset-2"
          >
            {isSubmitting ? 'Signing in...' : 'Sign in'}
          </button>

          {/* The provider is no longer known at build time, so this label
              cannot name one. */}
          <button
            type="button"
            onClick={() => setShowDevLogin(false)}
            className="w-full text-center text-xs text-faint-ink hover:text-content"
          >
            Back to sign-in
          </button>
        </form>
      ) : (
        <div className="space-y-4">
          {firm.isLoading && (
            <button type="button" disabled className={SSO_BUTTON_CLASS}>
              Sign in
            </button>
          )}

          {!firm.isLoading && lookupFailed && (
            <button type="button" onClick={signIn} className={SSO_BUTTON_CLASS}>
              Sign in
            </button>
          )}

          {!firm.isLoading && !lookupFailed && (connected || bootstrapEligible) && (
            <button type="button" onClick={signIn} className={SSO_BUTTON_CLASS}>
              {provider === 'GOOGLE' ? <GoogleMark /> : <MicrosoftMark />}
              {provider === 'GOOGLE' ? 'Sign in with Google' : 'Sign in with Microsoft'}
            </button>
          )}

          {/* First-admin bootstrap (design spec 2026-09-22, Component 5) —
              the button above still renders for a bootstrap-eligible firm
              (the designated admin can use it); this explains why it will
              refuse anyone else, alongside it rather than instead of it. */}
          {!firm.isLoading && !lookupFailed && !connected && (
            <p className="rounded-md bg-inset px-3 py-3 text-sm text-content">
              {notConnectedCopy}
            </p>
          )}

          {isMockAuth && (
            <button
              type="button"
              onClick={() => setShowDevLogin(true)}
              className="w-full text-center text-xs text-faint-ink hover:text-content"
            >
              Dev login
            </button>
          )}
        </div>
      )}

      <p className="mt-6 text-center text-xs text-muted">
        {firmName
          ? `Access is restricted to authorised ${firmName} staff.`
          : 'Access is restricted to authorised staff.'}
      </p>
    </div>
  );
}

/**
 * `useSearchParams` forces a Suspense boundary or the Next.js build fails
 * on this page — the same shape
 * `app/(dashboard)/admin/(people-access)/users/page.tsx`
 * uses.
 */
export default function LoginPage() {
  return (
    <Suspense
      fallback={
        <div className="w-full max-w-md rounded-lg bg-surface p-8 shadow-md">
          <div className="h-12 animate-pulse rounded bg-inset" />
        </div>
      }
    >
      <LoginPageContent />
    </Suspense>
  );
}
